import React, { useState } from 'react';
import { getCategorySelectionCounts } from '../../utils/indicatorCategories';
import { Edit2 } from 'lucide-react';

/**
 * CategoryAccordion Component
 *
 * Reusable accordion component for selecting indicators in a categorized structure.
 * Supports multiple display modes for different UI contexts.
 *
 * @param {object[]} categories - Array of category objects with indicators
 * @param {string[]} selectedItems - Array of selected indicator values
 * @param {function} onToggle - Callback when indicator is toggled: (indicatorValue) => void
 * @param {string} displayMode - 'list' | 'inline' | 'compact'
 * @param {function} onEdit - Optional callback when edit (pencil) icon is clicked: (indicatorValue) => void
 */
function CategoryAccordion({
  categories = [],
  selectedItems = [],
  onToggle,
  displayMode = 'list',
  onEdit = null
}) {
  // Track which categories are expanded (start with first 2 expanded)
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const initialExpanded = new Set();
    categories.slice(0, 2).forEach(cat => initialExpanded.add(cat.id));
    return initialExpanded;
  });

  // Track which indicator is being hovered (for pencil icon)
  const [hoveredIndicator, setHoveredIndicator] = useState(null);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Get selection counts per category
  const selectionCounts = getCategorySelectionCounts(selectedItems);

  // Render based on display mode
  if (displayMode === 'inline') {
    return renderInlineMode();
  } else if (displayMode === 'compact') {
    return renderCompactMode();
  } else {
    return renderListMode();
  }

  /**
   * List Mode: Standard accordion with checkboxes and descriptions
   * Used in: AddWidgetModal
   */
  function renderListMode() {
    return (
      <div className="space-y-2">
        {categories.map(category => {
          const isExpanded = expandedCategories.has(category.id);
          const selectedCount = selectionCounts[category.id] || 0;

          return (
            <div
              key={category.id}
              className="border border-muted rounded-lg overflow-hidden"
            >
              {/* Category Header */}
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <div className="font-medium text-text">{category.label}</div>
                    {category.description && (
                      <div className="text-xs text-gray-400">{category.description}</div>
                    )}
                  </div>
                  {selectedCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-600/30 text-blue-400 rounded-full">
                      {selectedCount} selected
                    </span>
                  )}
                </div>
                <span className="text-gray-400 text-sm">
                  {isExpanded ? '▲' : '▼'}
                </span>
              </button>

              {/* Category Content */}
              {isExpanded && (
                <div className="p-3 space-y-2 bg-background">
                  {category.indicators.map(indicator => {
                    const isSelected = selectedItems.includes(indicator.value);

                    return (
                      <label
                        key={indicator.value}
                        className="flex items-start gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggle(indicator.value)}
                          className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-text">{indicator.label}</div>
                          <div className="text-sm text-gray-400">{indicator.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * Inline Mode: Button grid per category
   * Used in: WidgetCard inline edit mode
   */
  function renderInlineMode() {
    return (
      <div className="space-y-3">
        {categories.map(category => {
          const isExpanded = expandedCategories.has(category.id);
          const selectedCount = selectionCounts[category.id] || 0;

          return (
            <div key={category.id} className="space-y-1">
              {/* Compact Category Header */}
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex items-center gap-2 text-xs text-gray-400 hover:text-text transition-colors"
              >
                <span>{category.label}</span>
                {selectedCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded">
                    {selectedCount}
                  </span>
                )}
                <span className="text-[10px]">{isExpanded ? '▲' : '▼'}</span>
              </button>

              {/* Button Grid */}
              {isExpanded && (
                <div className="flex flex-wrap gap-1">
                  {category.indicators.map(indicator => {
                    const isSelected = selectedItems.includes(indicator.value);

                    return (
                      <button
                        key={indicator.value}
                        type="button"
                        onClick={() => onToggle(indicator.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        title={indicator.description}
                      >
                        {indicator.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * Compact Mode: Minimal checkboxes without descriptions
   * Used in: WidgetCard dropdown menu
   */
  function renderCompactMode() {
    return (
      <div className="space-y-1">
        {categories.map(category => {
          const isExpanded = expandedCategories.has(category.id);
          const selectedCount = selectionCounts[category.id] || 0;

          return (
            <div key={category.id}>
              {/* Minimal Header */}
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-muted/50 rounded transition-colors text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-text">{category.label}</span>
                  {selectedCount > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-600/30 text-blue-400 rounded">
                      {selectedCount}
                    </span>
                  )}
                </div>
                <span className="text-gray-400 text-xs">
                  {isExpanded ? '▲' : '▼'}
                </span>
              </button>

              {/* Compact Checkboxes */}
              {isExpanded && (
                <div className="pl-6 pr-2 py-1 space-y-1">
                  {category.indicators.map(indicator => {
                    const isSelected = selectedItems.includes(indicator.value);

                    return (
                      <div
                        key={indicator.value}
                        className="flex items-center justify-between group hover:bg-muted/30 rounded cursor-pointer transition-colors text-sm py-1"
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseEnter={() => setHoveredIndicator(indicator.value)}
                        onMouseLeave={() => setHoveredIndicator(null)}
                      >
                        <label className="flex items-center gap-2 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggle(indicator.value)}
                            className="h-3.5 w-3.5 rounded border-gray-600 bg-gray-700 text-blue-600"
                          />
                          <span className="text-text">{indicator.label}</span>
                        </label>
                        {onEdit && isSelected && hoveredIndicator === indicator.value && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(indicator.value);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-white transition-colors px-2"
                            title="Edit settings"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

export default CategoryAccordion;
